import React, { useState, useEffect, useRef } from "react";
import {
  Table,
  ScrollArea,
  rem,
  Text,
  UnstyledButton,
  Group,
  Center,
  Select,
  Button,
  Checkbox,
  Pagination,
  Badge,
} from "@mantine/core";
import {
  IconSelector,
  IconChevronDown,
  IconChevronUp,
  IconTrash,
  IconEdit,
  IconBrandTabler,
  IconEye,
} from "@tabler/icons-react";
import { showErrorAlert, showSuccessAlert } from "../../helper/alerts";
import DeleteModal from "./DeleteModal";
import AddSplashScreenModal from "./AddSplashScreenModal";
import ViewSplashScreenModal from "./ViewSplashScreenModal";
import classes from "./SplashScreen.module.css";
import { RowDataSplashScreen } from "./types";
import { useFetchRowData } from "../hooks/useFetchRowData";
import { splashScreen } from "../types/RowDataTypes";
import { CustomTable } from "../Table/CustomTable";
import { SearchBar } from "../SearchBar/SearchBar";
import useAxiosPrivate from "../../auth/useAxiosPrivate";
import Header from "../static/Header";
import getSplashScreenTable from "../../DataDisplay/DataDisplayTable/SplashScreenTable";

// Searching and Sorting
enum ColumnHeaderToFilterPair {
  NAME = "Title",
  IMAGE = "IMG URL",
//   COLOR = "Color",
//   ROUTE = "Route",
  SHOW = "Show",
}

const colFilter = ColumnHeaderToFilterPair;
const colFilters: { [key in ColumnHeaderToFilterPair]: string } = {
  [colFilter.NAME]: "title",
  [colFilter.IMAGE]: "",
//   [colFilter.COLOR]: "color",
// [colFilter.ROUTE]: "route",
  [colFilter.SHOW]: "isVisible",
};

interface ThProps {
  children: React.ReactNode;
  sortable: boolean;
  reversed: boolean;
  sorted: boolean;
  onSort(): void;
}

function Th({ children, sortable, reversed, sorted, onSort }: ThProps) {
  const Icon = sorted
    ? reversed
      ? IconChevronUp
      : IconChevronDown
    : IconSelector;

  const allowedSorts = [
    // colFilter.NAME.toLowerCase(),
    // colFilter.ROUTE.toLowerCase(),
    // colFilter.SHOW.toLowerCase(),
  ];

  return (
    <Table.Th className={`${classes.th} ${sortable ? classes.sortable : ""}`}>
      
        <Text fw={500} fz="sm">
          {children}
        </Text>
      
    </Table.Th>
  );
}

const initialData: RowDataSplashScreen[] = [
  {
    id: "",
    title: "",
    img_url: "",
    isVisible: false,
    action: "",
  },
];

export function SplashScreen() {
  const axiosPrivate = useAxiosPrivate();

  const [search, setSearch] = useState("");
  const [tableData, setTableData] = useState(initialData);
  const [sortBy, setSortBy] = useState<keyof RowDataSplashScreen | null>(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  //Used in searching and when deleting to cause rerender to fetch data
  const [tableStateChanged, setTableStateChanged] = useState(0);

  const changeTableState = () => {
    setTableStateChanged(Math.random() * Math.random());
  };

  const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
  function resetPage() {
    if (currentPage !== 1) {
      setCurrentPage(1);
      return true;
    }
    return false;
  }

  const checkboxesFilter: JSX.Element[] = [];

  //Set allowed search filter checkbox to be shown
  const allowedFilters = [
    colFilters[colFilter.NAME],
    // colFilters[colFilter.ROUTE],
  ];
  for (const [key, value] of Object.entries(colFilters)) {
    if (allowedFilters.includes(value)) {
      const checkboxFilter: JSX.Element = (
        <Checkbox value={value} label={key} />
      );
      checkboxesFilter.push(checkboxFilter);
    }
  }

  const [showFilterOptions, setShowFilterOptions] = useState(false);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target.value;
    setSearch(input);
  };

  const toggleVisibility = async (row: RowDataSplashScreen, e: any) => {
    e.target.disabled = true;
    let endpoint;
    if (!row.isVisible) endpoint = "/api/admin/splash-screen/show";
    else endpoint = "/api/admin/splash-screen/hide";

    await axiosPrivate
      .post(endpoint, {
        id: row.id,
      })
      .then((response) => {
        changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error occured");
      });

    e.target.disabled = false;
  };

  const fetchedData = useFetchRowData(
    itemsPerPage,
    currentPage,
    search,
    selectedFilters,
    tableStateChanged,
    sortBy ?? "createdAt",
    reverseSortDirection ? "DESC" : "ASC",
    splashScreen,
    getSplashScreenTable().create(),
    "",
  );
  const itemsOnPage = useRef(0);
  const [totalRows, setTotalRows] = useState<number>(0);
  // const totalRows = fetchedData.totalRows;
  let rowData: RowDataSplashScreen[] | null = fetchedData.data as RowDataSplashScreen[];
  const isLoading: boolean = fetchedData.isLoading;
  const [rowDataPDFPriceList, setRowDataPDFPriceList] = useState<RowDataSplashScreen[] | null>(
    null
  );

  const [isAddSPModalOpen, setAddSPModalOpen] = useState(false);
  const [isEditBannerModalOpen, setEditBannerModalOpen] = useState(false);
  const [selectedRowForEdit, setSelectedRowForEdit] =
    useState<RowDataSplashScreen | null>(null);
  const [isViewSPModalOpen, setViewSPModalOpen] = useState(false);
  const [selectedRowForView, setSelectedRowForView] =
    useState<RowDataSplashScreen | null>(null);

  const [isDeleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedRowToDelete, setSelectedRowToDelete] =
    useState<RowDataSplashScreen | null>(null);

  const handleAddSP = () => {
    setAddSPModalOpen(true);
  };

  const handleAddSPModalClose = () => {
    setAddSPModalOpen(false);
  };

  const handleEdit = (row: RowDataSplashScreen) => {
    setSelectedRowForEdit(row);
    setEditBannerModalOpen(true);
  };

  const handleAddSPSuccess = (newData: RowDataSplashScreen) => {
    axiosPrivate
      .post(
        "/api/admin/splash-screen/create",
        {
          title: newData.title,
          isVisible: newData.isVisible,
          image: newData.img_url,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      )
      .then((response) => {
        changeTableState();
        showSuccessAlert("Splash Screen Successfully Added");
      })
      .catch((error) => {
        console.log("error " + error);
        showErrorAlert("Something Went Wrong");
      });
  };

//   const handleEditBannerSuccess = (editedBannerData: RowDataPDFPriceList) => {

//     let params: any = {
//       id: editedBannerData.id,
//       title: editedBannerData.title,
//     //   route: editedBannerData.pdf_url,
//     }

//     if (!(!editedBannerData.pdf_url.path))
//       params["image"] = editedBannerData.pdf_url


//     // console.log(params)
//     axiosPrivate
//       .post("/api/admin/banner/update", params,
//         {
//           headers: {
//             "Content-Type": "multipart/form-data",
//           },
//         })
//       .then((response) => {

//         // Find the index of the selectedRow in the sortedData
//         const indexOfSelectedRow = rowDataPDFPriceList!!.findIndex(
//           (row) => row === selectedRowForEdit
//         );

//         // Create a copy of the sortedData
//         const updatedData = [...rowDataPDFPriceList!!];
//         const data = response.data.links;

//         let dataToUpdate: any = {
//           ...selectedRowForEdit!!,
//           title: editedBannerData.title,
//         //   route: editedBannerData.route,
//         }

//         if (!(!data))
//           dataToUpdate.imageSrc = data

//         updatedData[indexOfSelectedRow] = dataToUpdate
//         // changeTableState();
//         setRowDataPDFPriceList(updatedData);
//         showSuccessAlert("Banner Successfully Edited");
//       })
//       .catch((error) => {
//         // console.log("error " + error);
//         showErrorAlert("Something Went Wrong");
//       });
//     setEditBannerModalOpen(false);
//   };

  const handleDelete = (row: RowDataSplashScreen) => {
    setSelectedRowToDelete(row);
    setDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmed = () => {
    axiosPrivate
      .post("/api/admin/splash-screen/delete", {
        id: selectedRowToDelete!!.id,
      })
      .then((response) => {
        setDeleteConfirmationOpen(false);
        changeTableState();
      })
      .catch((err) => {
        showErrorAlert("An error has occured");
      });

    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleDeleteCancelled = () => {
    setDeleteConfirmationOpen(false);
    setSelectedRowToDelete(null);
  };

  const handleView = (row: RowDataSplashScreen) => {
    setSelectedRowForView(row);
    setViewSPModalOpen(true);
  };

  const setSorting = (field: keyof RowDataSplashScreen) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    changeTableState();
  };

  const startIndex = (currentPage - 1) * itemsPerPage;

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleRowsPerPageChange = (value: number) => {
    setCurrentPage(1);
    setItemsPerPage(value);
  };

  useEffect(() => {
    setRowDataPDFPriceList(rowData);
    itemsOnPage.current = fetchedData.itemsOnPage;
    setTotalRows(fetchedData.totalRows);
  }, [rowData]);

  const rowsToMap = rowDataPDFPriceList ?? [];
  const rows = rowsToMap.map((row, index) => (
    <Table.Tr key={index}>
      <Table.Td>{startIndex + index + 1}</Table.Td>
      <Table.Td className={classes.titleColumn}>{row.title}</Table.Td>
      <Table.Td className={classes.imageLink}>{row.img_url}</Table.Td>
      {/* <Table.Td className={classes.colorAndButtonColumn}>{row.color}</Table.Td> */}
      {/* <Table.Td className={classes.colorAndButtonColumn}>{row.route}</Table.Td> */}
      <Table.Td className={classes.colorAndButtonColumn}>
        <Button
          className={classes.showButton}
          style={{
            backgroundColor: row.isVisible ? "#2BDD66" : "#0014AD",
          }}
          onClick={(e) => toggleVisibility(row, e)}
        >
          {row.isVisible ? "ON" : "OFF"}
        </Button>
      </Table.Td>
      <Table.Td className={classes.actionButtons}>
        <button onClick={() => handleView(row)} className={classes.viewButton}>
          <IconEye /> View
        </button>

        <button
          onClick={() => handleDelete(row)}
          className={classes.trashbutton}
        >
          <IconTrash /> Delete
        </button>
      </Table.Td>
    </Table.Tr>
  ));

  const handleSelectPage = (selectedPage: number) => {
    if (selectedPage != currentPage) setCurrentPage(selectedPage);
  };

  return (
    <div className={classes.bannerPageContainer}>
      <ScrollArea className={classes.parentContainer}>
        <Header title="Splash Screen" />
        <div className={classes.searchAndAddContainer}>
          <SearchBar
            isLoading={isLoading}
            resetPage={resetPage}
            changeTableState={changeTableState}
            search={search}
            handleSearch={handleSearch}
            setShowFilterOptions={setShowFilterOptions}
            showFilterOptions={showFilterOptions}
            selectedFilters={selectedFilters}
            setSelectedFilters={setSelectedFilters}
            checkboxesFilter={checkboxesFilter}
          />
          <div className={classes.AddButtonContainer}>
            <Button className={classes.addButton} onClick={handleAddSP}>
              <IconBrandTabler className={classes.iconBrandTabler} />
              Upload Image
            </Button>
          </div>
        </div>

        <ViewSplashScreenModal
          isOpen={isViewSPModalOpen}
          onClose={() => setViewSPModalOpen(false)}
          spData={selectedRowForView || initialData[0]}
        />

        <AddSplashScreenModal
          isOpen={isAddSPModalOpen}
          onClose={handleAddSPModalClose}
          onAddSplashScreen={handleAddSPSuccess}
        />


        <DeleteModal
          isOpen={isDeleteConfirmationOpen}
          onClose={handleDeleteCancelled}
          onConfirm={handleDeleteConfirmed}
        />

        <CustomTable
          colHeaders={Object.values(ColumnHeaderToFilterPair)}
          filters={Object.values(colFilters)}
          reverseSortDirection={reverseSortDirection}
          sortBy={sortBy}
          setSorting={setSorting}
          customclasses={classes}
          Th={Th}
          rowData={rowDataPDFPriceList}
          rows={rows}
        />

        <div className={classes.paginationContainer}>
          <div className={classes.resultCount}>
            <Text>
              {`Showing `}
              <span style={{ fontWeight: "bold" }}>{startIndex + 1}</span>
              {`-`}
              <span style={{ fontWeight: "bold" }}>
                {Math.min(currentPage * itemsPerPage, totalRows)}
              </span>
              {` of `}
              <span style={{ fontWeight: "bold" }}>{totalRows}</span>
              {` results`}
            </Text>
          </div>

          <div className={classes.rightContainer}>
            <div className={classes.rowsPerPageSelector}>
              <Select
                placeholder="Rows per page"
                data={["10", "20", "50"]}
                value={itemsPerPage.toString()}
                onChange={(value) =>
                  handleRowsPerPageChange(parseInt(value as string, 10))
                }
              />
            </div>

            <div className={classes.paginationSelector}>
              <Pagination
                color="blueviolet"
                value={currentPage}
                total={Math.ceil(totalRows / itemsPerPage)}
                onNextPage={() => handlePageChange(currentPage + 1)}
                onPreviousPage={() => handlePageChange(currentPage - 1)}
                onChange={(value: number) => handleSelectPage(value)}
              />
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}

export default SplashScreen;
